import React, { useState, useRef, useEffect } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import {
  FleetHealthChart,
  RiskDistributionChart,
  FleetRiskTrend,
} from "./FleetCharts";
import { InfoTooltip } from "./InfoTooltip";
import { TOOLTIPS } from "../utils/constants";

/**
 * Parse simple markdown (bold **text**) into React elements.
 */
function parseSimpleMarkdown(text) {
  if (!text) return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

/**
 * AIInsightsPanel - Shows AI-generated insights about the fleet
 */
function AIInsightsPanel({ assets, riskHistory }) {
  const assetList = Object.values(assets);

  // Find engines with explanations (up to 3)
  const enginesWithInsights = assetList
    .filter(a => a.risk?.explanation)
    .slice(0, 3);

  // Calculate fleet-wide metrics
  const avgRisk = assetList.length > 0
    ? assetList.reduce((sum, a) => sum + (a.risk?.risk_score || 0), 0) / assetList.length
    : 0;

  const criticalCount = assetList.filter(a => a.status === "ALERT").length;
  const warningCount = assetList.filter(a => a.status === "WARNING").length;

  if (assetList.length === 0) {
    return (
      <div className="ai-insights-empty">
        <span>Insights will appear here</span>
        <span className="hint">Start streaming to see predictions</span>
      </div>
    );
  }

  return (
    <div className="ai-insights-content">
      {/* Fleet Summary */}
      <div className="insight-section fleet-summary">
        <div className="insight-header">
          <span>Fleet Status</span>
        </div>
        <div className="fleet-risk-summary">
          <div className="risk-avg">
            <span className="risk-value">{(avgRisk * 100).toFixed(0)}%</span>
            <span className="risk-label">Avg Risk</span>
          </div>
          {criticalCount > 0 && (
            <div className="risk-alert critical">
              <ExclamationTriangleIcon className="h-3 w-3" />
              <span>{criticalCount} critical</span>
            </div>
          )}
          {warningCount > 0 && (
            <div className="risk-alert warning">
              <ExclamationTriangleIcon className="h-3 w-3" />
              <span>{warningCount} warning</span>
            </div>
          )}
        </div>
      </div>

      {/* AI Explanations */}
      {enginesWithInsights.length > 0 && (
        <div className="insight-section ai-explanations">
          <div className="insight-header">
            <span>Analysis</span>
          </div>
          <div className="explanations-list">
            {enginesWithInsights.map(engine => (
              <div key={engine.asset_id} className="explanation-item">
                <div className="explanation-header">
                  <span className="engine-id">Engine {engine.asset_id}</span>
                  <span className={`status-dot ${engine.status?.toLowerCase()}`}></span>
                </div>
                <p className="explanation-text">
                  {parseSimpleMarkdown(engine.risk?.explanation)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No insights yet */}
      {enginesWithInsights.length === 0 && criticalCount === 0 && warningCount === 0 && (
        <div className="insight-section all-clear">
          <div className="all-clear-content">
            <span className="all-clear-dot"></span>
            <span>All engines operating normally</span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * FleetDashboard - Main dashboard view showing fleet-wide metrics
 */
export function FleetDashboard({
  assets,
  serverState,
  recentEvents,
  riskHistory,
  onSelectAsset,
  isStarting,
}) {
  const [hoveredAsset, setHoveredAsset] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const terminalLogRef = useRef(null);

  // Auto-scroll terminal log to bottom when new events arrive
  useEffect(() => {
    if (terminalLogRef.current) {
      terminalLogRef.current.scrollTop = terminalLogRef.current.scrollHeight;
    }
  }, [recentEvents]);

  const assetList = Object.values(assets);
  const isStreaming = serverState?.streaming || false;

  // Calculate fleet stats
  const stats = {
    total: assetList.length,
    ok: assetList.filter((a) => a.status === "OK").length,
    warning: assetList.filter((a) => a.status === "WARNING").length,
    alert: assetList.filter((a) => a.status === "ALERT").length,
    warming: assetList.filter((a) => a.status === "WARMING" || a.status === "INIT").length,
  };

  // Sort assets for heatmap display
  const sortedAssets = [...assetList].sort((a, b) => a.asset_id - b.asset_id);

  // Loading state - after clicking start but before streaming begins
  if (isStarting) {
    return (
      <div className="fleet-dashboard loading">
        <div className="loading-state">
          <div className="loading-spinner">
            <div className="spinner-ring"></div>
          </div>
          <h3>Initializing Stream</h3>
          <p>Connecting to engine sensors...</p>
        </div>
      </div>
    );
  }

  if (!isStreaming && assetList.length === 0) {
    return (
      <div className="fleet-dashboard empty">
        <div className="empty-state">
          <h2>Real-Time Engine Monitoring</h2>
          <p>Stream sensor data and monitor failure risk in real-time</p>
          <div className="empty-features">
            <div className="feature">
              <span className="feature-dot"></span>
              <span>Live sensor streaming</span>
            </div>
            <div className="feature">
              <span className="feature-dot"></span>
              <span>Risk prediction</span>
            </div>
            <div className="feature">
              <span className="feature-dot"></span>
              <span>Early failure detection</span>
            </div>
          </div>
          <p className="start-hint">Click "Start Streaming" to begin</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fleet-dashboard">
      {/* Fleet Stats Bar */}
      <div className="fleet-stats-bar">
        <div className="stat-card total">
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Engines</span>
          </div>
        </div>
        <div className="stat-card ok">
          <div className="stat-content">
            <span className="stat-value">{stats.ok}</span>
            <span className="stat-label">Healthy</span>
          </div>
        </div>
        <div className="stat-card warning">
          <div className="stat-content">
            <span className="stat-value">{stats.warning}</span>
            <span className="stat-label">Warning</span>
          </div>
        </div>
        <div className="stat-card alert">
          <div className="stat-content">
            <span className="stat-value">{stats.alert}</span>
            <span className="stat-label">Critical</span>
          </div>
        </div>
        <div className="stat-card events">
          <div className="stat-content">
            <span className="stat-value">
              {(serverState?.events_sent || 0).toLocaleString()}
            </span>
            <span className="stat-label">Events</span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="fleet-charts-row">
        <div className="fleet-panel chart-panel">
          <div className="panel-title">
            <span>Fleet Health</span>
            <InfoTooltip text={TOOLTIPS.fleetHealth} />
          </div>
          <FleetHealthChart assets={assets} />
        </div>

        <div className="fleet-panel chart-panel">
          <div className="panel-title">
            <span>Risk Distribution</span>
            <InfoTooltip text={TOOLTIPS.riskDistribution} />
          </div>
          <RiskDistributionChart assets={assets} />
        </div>

        <div className="fleet-panel chart-panel">
          <div className="panel-title">
            <span>Fleet Risk Trend</span>
            <InfoTooltip text={TOOLTIPS.fleetRiskTrend} />
          </div>
          <FleetRiskTrend riskHistory={riskHistory || {}} />
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="fleet-content">
        {/* Left Column - Heatmap + Feed */}
        <div className="fleet-column main">
          <div className="fleet-panel heatmap-panel">
            <div className="panel-title">
              <span>Engine Status Grid</span>
              <span className="panel-hint">Click to view details</span>
            </div>
            <div className="heatmap-grid">
              {sortedAssets.map((asset) => (
                <div
                  key={asset.asset_id}
                  className={`heatmap-cell ${getStatusClass(asset.status)} ${
                    asset.risk?.alert ? "alerting" : ""
                  }`}
                  onClick={() => onSelectAsset(asset.asset_id)}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top });
                    setHoveredAsset(asset);
                  }}
                  onMouseLeave={() => setHoveredAsset(null)}
                >
                  <span className="cell-id">{asset.asset_id}</span>
                  {asset.risk?.risk_score !== undefined && (
                    <span className="cell-risk">
                      {(asset.risk.risk_score * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
              ))}

              {/* Custom Tooltip */}
              {hoveredAsset && (
                <div
                  className="engine-tooltip"
                  style={{
                    left: tooltipPos.x,
                    top: tooltipPos.y,
                  }}
                >
                  <div className="tooltip-header">
                    <span className="tooltip-engine">Engine {hoveredAsset.asset_id}</span>
                    <span className={`tooltip-status ${getStatusClass(hoveredAsset.status)}`}>
                      {hoveredAsset.status}
                    </span>
                  </div>
                  {hoveredAsset.risk && (
                    <div className="tooltip-risk">
                      <div className="tooltip-score">
                        <span className={`score-value ${getRiskClass(hoveredAsset.risk.risk_score)}`}>
                          {(hoveredAsset.risk.risk_score * 100).toFixed(0)}%
                        </span>
                        <span className="score-label">Risk</span>
                      </div>
                      {hoveredAsset.risk.explanation && (
                        <div className="tooltip-explanation">
                          {parseSimpleMarkdown(hoveredAsset.risk.explanation)}
                        </div>
                      )}
                      {!hoveredAsset.risk.explanation && hoveredAsset.risk.top_features?.[0] && (
                        <div className="tooltip-factors">
                          Top factor: {hoveredAsset.risk.top_features[0]}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="tooltip-hint">Click to view details</div>
                </div>
              )}
            </div>
            {sortedAssets.length === 0 && (
              <div className="heatmap-empty">
                <span className="loading-dots">...</span>
                <span>Waiting for engine data...</span>
              </div>
            )}
          </div>

          {/* Live Data Log - Terminal Style */}
          <div className="fleet-panel event-feed-panel terminal-panel">
            <div className="panel-title terminal-header">
              <span>Live Data Stream</span>
              <span className="terminal-status">● STREAMING</span>
            </div>
            <div className="terminal-log" ref={terminalLogRef}>
              {recentEvents.length === 0 ? (
                <div className="terminal-empty">
                  <span className="terminal-cursor">_</span>
                  <span>Awaiting sensor telemetry...</span>
                </div>
              ) : (
                recentEvents.slice(-12).map((event, idx, arr) => (
                  <div
                    key={`${event.asset_id}-${event.cycle}-${idx}-${event.type}`}
                    className={`terminal-line ${idx === arr.length - 1 ? "latest" : ""} line-${event.type}`}
                  >
                    <span className="line-num">{String(event.lineNum || 0).padStart(4, '0')}</span>
                    {event.type === 'sensor' ? (
                      <>
                        <span className="term-time">{event.timestamp}</span>
                        <span className="term-tag sensor">SENSOR</span>
                        <span className="term-id">Engine {event.asset_id}</span>
                        <span className="term-cycle">Cycle {event.cycle}</span>
                        <span className="term-separator">│</span>
                        <span className="term-data">
                          <span className="data-group temp">
                            <span className="data-label">T2:</span>
                            <span className="data-val">{event.sensors?.T2?.toFixed(2)}</span>
                            <span className="data-unit">°R</span>
                          </span>
                          <span className="data-group pressure">
                            <span className="data-label">P7:</span>
                            <span className="data-val">{event.sensors?.P7?.toFixed(2)}</span>
                            <span className="data-unit">psia</span>
                          </span>
                          <span className="data-group speed">
                            <span className="data-label">N2:</span>
                            <span className="data-val">{event.sensors?.N2?.toFixed(0)}</span>
                            <span className="data-unit">rpm</span>
                          </span>
                          <span className="data-group fuel">
                            <span className="data-label">FF:</span>
                            <span className="data-val">{event.sensors?.FF?.toFixed(2)}</span>
                          </span>
                          <span className="data-group bleed">
                            <span className="data-label">W21:</span>
                            <span className="data-val">{event.sensors?.W21?.toFixed(2)}</span>
                            <span className="data-unit">lb/s</span>
                          </span>
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="term-time">{event.timestamp}</span>
                        <span className="term-tag risk">RISK</span>
                        <span className="term-id">Engine {event.asset_id}</span>
                        <span className="term-cycle">Cycle {event.cycle}</span>
                        <span className="term-separator">│</span>
                        <span className={`term-risk-score ${getRiskClass(event.risk)}`}>
                          risk:{(event.risk * 100).toFixed(1)}%
                        </span>
                        <span className="term-conf">conf:{((event.confidence || 0) * 100).toFixed(0)}%</span>
                        <span className="term-features">
                          features:[{event.top_features?.slice(0, 2).join(", ")}]
                        </span>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Column - AI Insights */}
        <div className="fleet-column side">
          <div className="fleet-panel ai-insights-panel full-height">
            <div className="panel-title">
              <span>Insights</span>
            </div>
            <AIInsightsPanel assets={assets} riskHistory={riskHistory} />
          </div>
        </div>
      </div>
    </div>
  );
}

function getStatusClass(status) {
  switch (status) {
    case "OK":
      return "status-ok";
    case "WARNING":
      return "status-warning";
    case "ALERT":
      return "status-alert";
    case "WARMING":
    case "INIT":
      return "status-warming";
    default:
      return "";
  }
}

function getRiskClass(riskScore) {
  if (riskScore >= 0.7) return "risk-critical";
  if (riskScore >= 0.5) return "risk-elevated";
  if (riskScore >= 0.3) return "risk-moderate";
  return "risk-low";
}
